import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import { 
  Heart, 
  MessageCircle, 
  MapPin, 
  Bike, 
  Image as ImageIcon, 
  Send, 
  MoreHorizontal,
  Trash2,
  X,
  Upload,
  User
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Feed = () => {
  const { t, i18n } = useTranslation();
  const { user, token } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newPostOpen, setNewPostOpen] = useState(false);
  const [newPost, setNewPost] = useState({ content: '', images: [], location: '', ride_km: '' });
  const [posting, setPosting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API_URL}/posts?feed=true`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingImage(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/upload/image`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
      });
      
      const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
      setNewPost(prev => ({ ...prev, images: [...prev.images, imageUrl] }));
      toast.success(t('common.success'));
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error(t('common.error'));
    } finally {
      setUploadingImage(false);
    }
  };

  const removeImage = (index) => {
    setNewPost(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleCreatePost = async () => {
    if (!newPost.content.trim() && newPost.images.length === 0) {
      toast.error(i18n.language === 'fr' ? 'Ajoutez du contenu ou une image' : 'Add content or an image');
      return;
    }

    setPosting(true);
    try {
      const payload = {
        content: newPost.content,
        images: newPost.images,
        location: newPost.location || null,
        ride_km: newPost.ride_km ? parseFloat(newPost.ride_km) : null,
      };

      await axios.post(`${API_URL}/posts`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success(t('common.success'));
      setNewPostOpen(false);
      setNewPost({ content: '', images: [], location: '', ride_km: '' });
      fetchPosts();
    } catch (error) {
      console.error('Error creating post:', error);
      toast.error(t('common.error'));
    } finally {
      setPosting(false);
    }
  };

  const handleLike = async (postId) => {
    try {
      const response = await axios.post(
        `${API_URL}/posts/${postId}/like`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setPosts(prev => prev.map(post => {
        if (post.id === postId) {
          return {
            ...post,
            liked_by_me: response.data.liked,
            likes_count: response.data.liked ? post.likes_count + 1 : post.likes_count - 1
          };
        }
        return post;
      }));
    } catch (error) {
      console.error('Error liking post:', error);
    }
  };

  const handleDeletePost = async (postId) => {
    try {
      await axios.delete(`${API_URL}/posts/${postId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPosts(prev => prev.filter(p => p.id !== postId));
      toast.success(t('common.success'));
    } catch (error) {
      console.error('Error deleting post:', error);
      toast.error(t('common.error'));
    }
  };

  const openComments = async (post) => {
    setSelectedPost(post);
    try {
      const response = await axios.get(`${API_URL}/posts/${post.id}/comments`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setComments(response.data);
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedPost) return;

    try {
      const response = await axios.post(
        `${API_URL}/posts/${selectedPost.id}/comments`,
        { content: newComment },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setComments(prev => [...prev, response.data]);
      setNewComment('');
      
      // Update comment count
      setPosts(prev => prev.map(post => {
        if (post.id === selectedPost.id) {
          return { ...post, comments_count: post.comments_count + 1 };
        }
        return post;
      }));
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error(t('common.error'));
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return format(date, 'PPp', { locale: i18n.language === 'fr' ? fr : enUS });
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-heading font-bold text-3xl uppercase tracking-tight">
          {t('social.feed')}
        </h1>
        <Button 
          onClick={() => setNewPostOpen(true)} 
          className="brand-glow"
          data-testid="new-post-btn"
        >
          {t('social.newPost')}
        </Button>
      </div>

      {/* New Post Dialog */}
      <Dialog open={newPostOpen} onOpenChange={setNewPostOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold text-xl uppercase">
              {t('social.shareRide')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <Textarea
              value={newPost.content}
              onChange={(e) => setNewPost(prev => ({ ...prev, content: e.target.value }))}
              placeholder={t('social.whatsOnYourMind')}
              rows={4}
              data-testid="post-content-input"
            />
            
            {/* Image Preview */}
            {newPost.images.length > 0 && (
              <div className="grid grid-cols-2 gap-2">
                {newPost.images.map((img, idx) => (
                  <div key={idx} className="relative aspect-video rounded-md overflow-hidden">
                    <img src={img} alt="" className="w-full h-full object-cover" />
                    <button
                      onClick={() => removeImage(idx)}
                      className="absolute top-2 right-2 w-6 h-6 rounded-full bg-black/60 flex items-center justify-center"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Optional fields */}
            <div className="grid grid-cols-2 gap-4">
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  value={newPost.location}
                  onChange={(e) => setNewPost(prev => ({ ...prev, location: e.target.value }))}
                  placeholder={i18n.language === 'fr' ? 'Lieu' : 'Location'}
                  className="pl-10"
                  data-testid="post-location-input"
                />
              </div>
              <div className="relative">
                <Bike className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="number"
                  value={newPost.ride_km}
                  onChange={(e) => setNewPost(prev => ({ ...prev, ride_km: e.target.value }))}
                  placeholder="Km"
                  className="pl-10"
                  data-testid="post-km-input"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between pt-4 border-t border-border">
              <div className="flex gap-2">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageUpload}
                  accept="image/*"
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingImage || newPost.images.length >= 4}
                  data-testid="upload-image-btn"
                >
                  {uploadingImage ? (
                    <div className="animate-spin w-4 h-4 border-2 border-primary border-t-transparent rounded-full" />
                  ) : (
                    <ImageIcon className="w-4 h-4" />
                  )}
                </Button>
              </div>
              <Button
                onClick={handleCreatePost}
                disabled={posting || (!newPost.content.trim() && newPost.images.length === 0)}
                data-testid="submit-post-btn"
              >
                {posting ? t('common.loading') : t('common.save')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Comments Dialog */}
      <Dialog open={!!selectedPost} onOpenChange={() => setSelectedPost(null)}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold text-xl uppercase">
              {t('social.comments')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Comments List */}
            <div className="space-y-3 max-h-[40vh] overflow-y-auto">
              {comments.length > 0 ? (
                comments.map((comment) => (
                  <div key={comment.id} className="flex gap-3">
                    <Avatar className="w-8 h-8">
                      <AvatarImage src={comment.avatar_url} />
                      <AvatarFallback><User className="w-4 h-4" /></AvatarFallback>
                    </Avatar>
                    <div className="flex-1 bg-secondary rounded-lg p-3">
                      <p className="font-medium text-sm">{comment.username}</p>
                      <p className="text-sm text-muted-foreground">{comment.content}</p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-center text-muted-foreground py-4">
                  {i18n.language === 'fr' ? 'Aucun commentaire' : 'No comments yet'}
                </p>
              )}
            </div>

            {/* Add Comment */}
            <div className="flex gap-2 pt-4 border-t border-border">
              <Input
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder={t('social.comment') + '...'}
                onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
                data-testid="comment-input"
              />
              <Button onClick={handleAddComment} size="icon" data-testid="send-comment-btn">
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Posts Feed */}
      {loading ? (
        <div className="space-y-6">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-4">
                <div className="flex gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-muted" />
                  <div className="flex-1">
                    <div className="h-4 bg-muted rounded w-1/4 mb-2" />
                    <div className="h-3 bg-muted rounded w-1/6" />
                  </div>
                </div>
                <div className="h-20 bg-muted rounded" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : posts.length > 0 ? (
        <div className="space-y-6">
          <AnimatePresence>
            {posts.map((post, index) => (
              <motion.div
                key={post.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card className="overflow-hidden" data-testid={`post-${post.id}`}>
                  <CardContent className="p-4">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-4">
                      <Link to={`/profile/${post.user_id}`} className="flex gap-3">
                        <Avatar className="w-10 h-10">
                          <AvatarImage src={post.avatar_url} />
                          <AvatarFallback><User className="w-5 h-5" /></AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium">{post.username}</p>
                          <p className="text-xs text-muted-foreground">{formatDate(post.created_at)}</p>
                        </div>
                      </Link>
                      
                      {post.user_id === user?.id && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => handleDeletePost(post.id)}
                              className="text-destructive"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              {t('common.delete')}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>

                    {/* Content */}
                    <p className="whitespace-pre-line mb-4">{post.content}</p>

                    {/* Images */}
                    {post.images?.length > 0 && (
                      <div className={`grid gap-2 mb-4 ${post.images.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                        {post.images.map((img, idx) => (
                          <img
                            key={idx}
                            src={img}
                            alt=""
                            className="w-full rounded-lg object-cover max-h-80"
                          />
                        ))}
                      </div>
                    )}

                    {/* Meta */}
                    {(post.location || post.ride_km) && (
                      <div className="flex gap-4 mb-4 text-sm text-muted-foreground">
                        {post.location && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {post.location}
                          </span>
                        )}
                        {post.ride_km && (
                          <span className="flex items-center gap-1">
                            <Bike className="w-4 h-4" />
                            {post.ride_km} {t('social.km')}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex items-center gap-4 pt-4 border-t border-border">
                      <button
                        onClick={() => handleLike(post.id)}
                        className={`flex items-center gap-2 transition-colors ${
                          post.liked_by_me ? 'text-red-500' : 'text-muted-foreground hover:text-foreground'
                        }`}
                        data-testid={`like-btn-${post.id}`}
                      >
                        <Heart className={`w-5 h-5 ${post.liked_by_me ? 'fill-current' : ''}`} />
                        <span className="text-sm">{post.likes_count}</span>
                      </button>
                      <button
                        onClick={() => openComments(post)}
                        className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
                        data-testid={`comment-btn-${post.id}`}
                      >
                        <MessageCircle className="w-5 h-5" />
                        <span className="text-sm">{post.comments_count}</span>
                      </button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      ) : (
        <Card className="bg-card/50">
          <CardContent className="p-12 text-center">
            <Bike className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-heading font-bold text-xl uppercase">{t('social.noPosts')}</h3>
            <p className="text-muted-foreground mt-2">
              {i18n.language === 'fr' 
                ? 'Partagez votre première balade !' 
                : 'Share your first ride!'}
            </p>
            <Button className="mt-6" onClick={() => setNewPostOpen(true)}>
              {t('social.newPost')}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Feed;
